<template>
  <div ref="styledTagsInput" class="tag-editor">
    <div class="tag-container" :style="componentStyle">
      <styled-tags-input
        class="tags-input"
        :add-only-from-autocomplete="addOnlyFromAutocomplete()"
        :autocomplete-items="getPossibleFieldValues()"
        :model-value="currentInput"
        :separators="[valuesSeparator]"
        :tag-classes="tagClasses"
        @before-destroy="saveInput"
        @tags-changed="handleTagsChanged"
      ></styled-tags-input>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted } from 'vue';
  import StyledTagsInput from '@/components/StyledTagsInput/StyledTagsInput.vue';
  import {
    DEFAULT_METADATA_VALUES_SEPARATOR,
    IDocumentMetadataWithFieldMetadataDto,
    IMetadataAndFieldMetadata,
  } from 'types';
  import { nextTick } from 'vue';
  import { Ref } from 'vue';
  import { CSSProperties } from 'vue';
  import { TagClass } from '@/components/StyledTagsInput/StyledTagsInput.types';
  import { normalizeTermName } from 'utils';

  const props = defineProps<{
    close: Function;
    model: Record<string, any>;
    prop: string;
    rowIndex: number;
    save: Function;
    fieldInfo: Ref<IDocumentMetadataWithFieldMetadataDto[]>;
  }>();

  const componentStyle = ref<CSSProperties>({
    position: 'fixed',
    top: '0px',
    left: '0px',
  });
  const currentInput = ref<string[]>([]);
  const tagClasses = ref<Record<string, TagClass>>({});
  const valuesSeparator = ref(DEFAULT_METADATA_VALUES_SEPARATOR);

  function setCurrentTags(): void {
    const tags: string = props.model[props.prop];
    /**
     * If after editing the cell no value is assigned, the editor will by
     * default assign an empty string as the value. In this case this empty
     * string must be ignored.
     */
    if (!tags) {
      currentInput.value = [];
      return;
    }

    currentInput.value = tags.split(DEFAULT_METADATA_VALUES_SEPARATOR);
    setTagClasses();
  }

  async function handleTagsChanged(input?: string[]): Promise<void> {
    if (!input) return;
    currentInput.value = input;
    setTagClasses();
  }

  function saveInput(): void {
    props.save(currentInput.value.join(valuesSeparator.value));
  }

  function setTagClasses(): void {
    tagClasses.value = {};
    currentInput.value.forEach((tag) => {
      const fieldInfo = getFieldInfoByFieldName(props.prop);
      const possibleValues = fieldInfo?.possibleValues?.map((v) =>
        normalizeTermName(v),
      );
      const isValid = possibleValues?.includes(normalizeTermName(tag));
      tagClasses.value[tag] = isValid ? TagClass.SUCCESS : TagClass.DANGER;
    });
  }

  function getFieldInfoByFieldName(
    fieldName: string,
  ): IMetadataAndFieldMetadata | undefined {
    return props.fieldInfo.value?.[0].metadata.find(
      (field) => field.fieldName === fieldName,
    );
  }

  function getPossibleFieldValues(): string[] | undefined {
    const fieldInfo = getFieldInfoByFieldName(props.prop);
    return fieldInfo?.possibleValues ?? [];
  }

  function addOnlyFromAutocomplete(): boolean {
    return !!getPossibleFieldValues()?.length;
  }

  const styledTagsInput = ref<HTMLElement>();
  function positionTagInput(): void {
    nextTick(() => {
      const element = styledTagsInput.value;

      const containingCellWidth = (
        (element as unknown as HTMLElement)?.parentNode
          ?.parentNode as HTMLElement
      )?.getBoundingClientRect().width;

      const rect = (element as unknown as HTMLElement)?.getBoundingClientRect();

      /**
       * Opening tag input a little higher if there are many tags so that the
       * end of the input always fits on the screen.
       */
      const topOffset = `${
        rect.top - (props.model[props.prop]?.length > 40 ? 100 : 0)
      }px`;
      const leftOffset = `${rect.left}px`;
      const tagInputWidth = `${containingCellWidth}px`;

      componentStyle.value.top = topOffset;
      componentStyle.value.left = leftOffset;
      componentStyle.value.width = tagInputWidth;
    });
  }

  onMounted(() => {
    setCurrentTags();
    positionTagInput();
  });
</script>

<style lang="scss" scoped>
  .tag-container {
    max-height: 300px;
    overflow: auto;

    .tags-input {
      :deep(.ti-autocomplete) {
        position: fixed;
        width: auto;
        max-height: 200px;
        overflow: auto;
      }
    }
  }
</style>
